import {FaultTreeNode} from '../../../../../../@types/faultTreeNode';

/** Barrier row used for rendering the hierarchical table */
export interface BarrierRow {
    node: FaultTreeNode;
    depth: number;
    hasBarrierDescendants: boolean;
}

/**
 * Build depth-first-ordered barrier rows with hierarchy information.
 *
 * For each barrier node, walk up via parentId to find the nearest barrier ancestor
 * (skipping gates, base events, etc.). Barriers with no barrier ancestor are top-level.
 * Returns rows in fault-tree depth-first traversal order.
 */
export function buildBarrierHierarchy(faultTreeNodes: FaultTreeNode[]): {
    rows: BarrierRow[];
    childrenMap: Map<number, number[]>;
} {
    const nodeById = new Map<number, FaultTreeNode>();
    faultTreeNodes.forEach((n) => nodeById.set(n.id, n));

    const barriers = faultTreeNodes.filter((n) => n.nodeClass === 'BARRIER');
    const barrierIdSet = new Set(barriers.map((b) => b.id));

    // For each barrier, find its nearest barrier ancestor
    const barrierParentMap = new Map<number, number | null>(); // barrierId -> nearest barrier ancestor id (or null)
    barriers.forEach((barrier) => {
        let current = barrier.parentId != null ? nodeById.get(barrier.parentId) : undefined;
        let foundAncestor: number | null = null;
        while (current) {
            if (barrierIdSet.has(current.id)) {
                foundAncestor = current.id;
                break;
            }
            current = current.parentId != null ? nodeById.get(current.parentId) : undefined;
        }
        barrierParentMap.set(barrier.id, foundAncestor);
    });

    // Build children map: barrier parent -> barrier children
    const childrenMap = new Map<number, number[]>();
    barriers.forEach((b) => {
        const parentBarrierId = barrierParentMap.get(b.id);
        if (parentBarrierId != null) {
            const children = childrenMap.get(parentBarrierId) || [];
            children.push(b.id);
            childrenMap.set(parentBarrierId, children);
        }
    });

    // Identify top-level barriers (no barrier ancestor)
    const topLevelBarriers = barriers.filter((b) => barrierParentMap.get(b.id) == null);

    // Check if a barrier has any barrier descendants (recursive)
    const hasBarrierDescendantsCache = new Map<number, boolean>();
    function hasDescendants(barrierId: number): boolean {
        if (hasBarrierDescendantsCache.has(barrierId)) {
            return hasBarrierDescendantsCache.get(barrierId)!;
        }
        const children = childrenMap.get(barrierId) || [];
        const result = children.length > 0;
        hasBarrierDescendantsCache.set(barrierId, result);
        return result;
    }

    // Compute actual fault tree depth for each node (walk up parentId to root)
    const faultTreeDepth = new Map<number, number>();
    function getDepth(nodeId: number): number {
        if (faultTreeDepth.has(nodeId)) return faultTreeDepth.get(nodeId)!;
        const node = nodeById.get(nodeId);
        if (!node || node.parentId == null || !nodeById.has(node.parentId)) {
            faultTreeDepth.set(nodeId, 0);
            return 0;
        }
        const d = getDepth(node.parentId) + 1;
        faultTreeDepth.set(nodeId, d);
        return d;
    }
    barriers.forEach((b) => getDepth(b.id));

    // Sort by fault tree depth ascending, then alphabetically by uniqueId
    const depthSort = (aId: number, bId: number): number => {
        const depthA = faultTreeDepth.get(aId) ?? 0;
        const depthB = faultTreeDepth.get(bId) ?? 0;
        if (depthA !== depthB) return depthA - depthB;
        const nodeA = nodeById.get(aId);
        const nodeB = nodeById.get(bId);
        return (nodeA?.uniqueId || '').localeCompare(nodeB?.uniqueId || '');
    };

    topLevelBarriers.sort((a, b) => depthSort(a.id, b.id));

    childrenMap.forEach((children) => {
        children.sort(depthSort);
    });

    // Build flat rows list with depth
    const rows: BarrierRow[] = [];
    function addRows(barrierIds: number[], depth: number): void {
        barrierIds.forEach((id) => {
            const node = nodeById.get(id);
            if (!node) return;
            rows.push({
                node,
                depth,
                hasBarrierDescendants: hasDescendants(id),
            });
        });
    }
    addRows(
        topLevelBarriers.map((b) => b.id),
        0
    );

    return {rows, childrenMap};
}

/**
 * Recursively collect visible rows based on the expanded set.
 */
export function getVisibleRows(
    topLevelIds: number[],
    childrenMap: Map<number, number[]>,
    nodeById: Map<number, FaultTreeNode>,
    expandedSet: Set<number>,
    hasBarrierDescendantsMap: Map<number, boolean>
): BarrierRow[] {
    const result: BarrierRow[] = [];

    function walk(ids: number[], depth: number): void {
        ids.forEach((id) => {
            const node = nodeById.get(id);
            if (!node) return;
            const hasBD = hasBarrierDescendantsMap.get(id) ?? false;
            result.push({node, depth, hasBarrierDescendants: hasBD});
            if (expandedSet.has(id)) {
                const children = childrenMap.get(id) || [];
                walk(children, depth + 1);
            }
        });
    }

    walk(topLevelIds, 0);
    return result;
}
